import React from 'react';
import {Box, Typography, Button, Container, Stack, alpha, useTheme} from '@mui/material';
import {useAuth0} from "@auth0/auth0-react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import UploadMenuSection from './UploadMenuSection';

const HeroSection = () => {
  const {t} = useTranslation();
  const {loginWithRedirect} = useAuth0();
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'background.default',
        pt: {xs: 12, md: 16},
        pb: {xs: 8, md: 12},
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '140%',
          height: '100%',
          background: `radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 60%)`,
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Badge/Label */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              mb: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: '100px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'primary.main',
              animation: 'fadeInUp 0.6s ease-out',
              '@keyframes fadeInUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(20px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            ✨ {t("Launch your digital menu in minutes")}
          </Box>

          {/* Main Headline */}
          <Typography
            variant="h1"
            sx={{
              fontSize: {xs: '2.5rem', sm: '3.5rem', md: '4.5rem'},
              fontWeight: 800,
              lineHeight: 1.1,
              mb: 3,
              background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.7)} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'fadeInUp 0.8s ease-out 0.1s both',
              letterSpacing: '-0.02em',
            }}
          >
            {t("Start your dream business")}<br />
            <Box component="span" sx={{color: 'primary.main'}}>
              {t("fast, easy, now")}
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h5"
            sx={{
              fontSize: {xs: '1.1rem', sm: '1.3rem', md: '1.5rem'},
              fontWeight: 400,
              lineHeight: 1.6,
              color: 'text.secondary',
              mb: 5,
              maxWidth: '800px',
              mx: 'auto',
              animation: 'fadeInUp 1s ease-out 0.2s both',
            }}
          >
            {t("QRseller lets small businesses go digital in minutes.")}
            <br />
            {t("Clients scan, order, pay — no lines, no talking, no hassle.")}
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={2}
            justifyContent="center"
            sx={{
              mb: 8,
              animation: 'fadeInUp 1.2s ease-out 0.3s both',
            }}
          >
            <Link
              to="/"
              onClick={() => loginWithRedirect({screen_hint: 'signup'})}
              style={{textDecoration: 'none'}}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                {t("Create menu in 3 minutes")}
              </Button>
            </Link>

            <Button
              variant="outlined"
              size="large"
              component="a"
              href="https://qrseller-client.netlify.app/?restaurant_id=2"
              target="_blank"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              {t("View demo")}
            </Button>
          </Stack>

          {/* Загрузка/съёмка фото меню → распознавание → создание учётной записи */}
          <Box sx={{ mb: 8 }}>
            <UploadMenuSection />
          </Box>

          {/* Social Proof / Stats */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: {xs: 3, md: 6},
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              animation: 'fadeInUp 1.4s ease-out 0.4s both',
            }}
          >
            <Box sx={{textAlign: 'center'}}>
              <Typography variant="h4" sx={{fontWeight: 700, color: 'primary.main', mb: 0.5}}>
                3 min
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("Setup time")}
              </Typography>
            </Box>
            <Box sx={{textAlign: 'center'}}>
              <Typography variant="h4" sx={{fontWeight: 700, color: 'primary.main', mb: 0.5}}>
                100+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("Active businesses")}
              </Typography>
            </Box>
            <Box sx={{textAlign: 'center'}}>
              <Typography variant="h4" sx={{fontWeight: 700, color: 'primary.main', mb: 0.5}}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("Support")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;